import { useQueries } from '@tanstack/react-query'
import { Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { accountApi } from '../api/client'
import { useTimeframe } from '../hooks/useTimeframe'
import { toEquityRange } from '../lib/timeframe'
import { indexSeries, type IndexedRow } from '../lib/indexSeries'
import type { Book } from '../types'
import './FleetValueOverlay.css'

// Data-viz palette by book ORDER (index.css:109-111) — never --gain/--loss,
// up/down colouring is meaningless when several books share one axis.
const SERIES_COLORS = [
  'var(--series-1)', 'var(--series-2)', 'var(--series-3)', 'var(--series-4)',
  'var(--series-5)', 'var(--series-6)', 'var(--series-7)', 'var(--series-8)',
]

export interface FleetLine { key: string; label: string; color: string }

export interface FleetIndexedSeries {
  rows: IndexedRow[]
  base: string | null
  lines: FleetLine[]
  excluded: string[]
  isLoading: boolean
  isError: boolean
}

/**
 * Fleet-wide indexed-value series: every book rebased to 100 at a common
 * base date (§2.3b — the LATEST first-date across books, never each book's
 * own first point) so lines that both leave 100 do so at the *same*
 * x-position and are actually comparable. Ranged by the same global
 * timeframe (`useTimeframe()`) as `BookValueChart` — no local range toggle.
 * Deliberately does NOT poll (staleTime: 300000, no refetchInterval, §2.4) —
 * N book queries firing every 30s would hammer the broker's rate limit.
 *
 * Takes the roster as an argument rather than calling useBooks() itself, so
 * it is usable standalone without stubbing /api/v1/accounts + /engine/status.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useFleetIndexedSeries(books: Book[]): FleetIndexedSeries {
  const { timeframe } = useTimeframe()
  const range = toEquityRange(timeframe)

  const results = useQueries({
    queries: books.map((b) => ({
      queryKey: ['equity-history', b.id, range],
      queryFn: () => accountApi.history(b.id, range),
      retry: false,
      staleTime: 300000,          // §2.4 — and NO refetchInterval, see below
    })),
  })
  const series = books.map((b, i) => ({
    key: `book-${b.id}`,          // `book-` prefix is load-bearing (§2.3b)
    points: (results[i].data?.points ?? []).map((p) => ({
      t: p.t,
      equity: Number(p.equity),   // wire type is a decimal STRING (§2.1)
    })),
  }))
  const { rows, base } = indexSeries(series)

  // Paused before any data (offline) is still loading, never "no history"
  // (plan Review Focus 2 — same rule as desk/fleetModuleHooks stillLoading).
  const isLoading = results.some((r) => r.isLoading || (r.isPaused && r.data === undefined))
  const isError = results.length > 0 && results.every((r) => r.isError)

  // A book only gets a <Line> if it clears the base-anchor rule (§2.3b) AND
  // contributes >= 2 rows — dot={false} makes a one-datum series an
  // invisible zero-length line that is present in the row set and would
  // otherwise escape this exclusion caption. Every excluded book is named,
  // distinguishing "never had data" from "dropped at the base date".
  const lines: FleetLine[] = []
  const excluded: string[] = []
  books.forEach((b, i) => {
    const key = `book-${b.id}`
    const label = b.label ?? `Account ${b.id}`
    const rawCount = series[i].points.length
    const contributed = rows.filter((row) => row[key] !== undefined).length
    if (contributed >= 2) {
      lines.push({ key, label, color: SERIES_COLORS[i % SERIES_COLORS.length] })
    } else if (contributed === 0 && rawCount > 0) {
      excluded.push(`${label}: no equity at base`)
    } else {
      excluded.push(`${label}: no history`)
    }
  })

  return { rows, base, lines, excluded, isLoading, isError }
}

interface FleetValueChartProps {
  rows: IndexedRow[]
  lines: FleetLine[]
  /** Size to the parent flex cell (height 100%) instead of the fixed 260px. */
  fill?: boolean
  showLegend?: boolean
}

export function FleetValueChart({ rows, lines, fill = false, showLegend = true }: FleetValueChartProps) {
  const chart = (
    <ResponsiveContainer width="100%" height={fill ? '100%' : 260}>
      <LineChart data={rows}>
        <XAxis dataKey="t" stroke="var(--fg-subtle)" fontSize={11} />
        <YAxis stroke="var(--fg-subtle)" fontSize={11} domain={['auto', 'auto']} />
        <Tooltip />
        <ReferenceLine y={100} stroke="var(--border-strong)" />
        {showLegend && <Legend />}
        {lines.map((l) => (
          <Line
            key={l.key}
            type="monotone"
            dataKey={l.key}
            name={l.label}
            stroke={l.color}
            dot={false}
            strokeWidth={2}
            connectNulls
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  )
  return fill ? <div className="chart-fill">{chart}</div> : chart
}
